import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type AttendanceState =
  'not_clocked_in' | 'clocked_in' | 'complete' | 'missing_clock_out';

export interface AttendanceTimestamps {
  clockedInAt: string | null;
  clockedOutAt: string | null;
}

export interface AttendanceRecord {
  id: string;
  staffUserId: string;
  staffName?: string;
  branchId: string;
  date: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  clockedInAt: string | null;
  clockedOutAt: string | null;
  clockInReason: string | null;
  clockOutReason: string | null;
  workedMinutes: number | null;
  lateMinutes: number;
  earlyMinutes: number;
  state: AttendanceState;
  late: boolean;
  earlyDeparture: boolean;
  missingClockOut: boolean;
  /** The timestamps recorded before any manager correction. */
  originalTimestamps: AttendanceTimestamps;
  /** The effective manager-entered timestamps, or null when never corrected. */
  correctedTimestamps: AttendanceTimestamps | null;
  correctedAt?: string | null;
  correctedBy?: string | null;
  correctionReason?: string | null;
}

export interface StaffAttendance {
  today: AttendanceRecord | null;
  history: AttendanceRecord[];
  clockInReasonRequired: boolean;
  clockOutReasonRequired: boolean;
}

export interface AttendanceActionInput {
  branchId: string;
  reason?: string;
}

export interface AttendanceFilters {
  branchId?: string;
  from?: string;
  to?: string;
}

export interface AttendanceCorrectionInput {
  id: string;
  clockedInAt: string;
  clockedOutAt: string | null;
  reason: string;
}

export interface CorrectedAttendanceRecord extends AttendanceRecord {
  correctedTimestamps: AttendanceTimestamps;
  correctedAt: string;
  correctedBy: string;
  correctionReason: string;
}

export const attendanceKeys = {
  all: ['attendance'] as const,
  mine: ['attendance', 'mine'] as const,
  manager: (filters: AttendanceFilters) =>
    ['attendance', 'manager', filters] as const,
};

export function useMyAttendance(enabled = true) {
  return useQuery({
    queryKey: attendanceKeys.mine,
    queryFn: ({ signal }) =>
      api
        .get<StaffAttendance>('/attendance/me', { signal })
        .then((response) => response.data),
    enabled,
  });
}

function useAttendanceAction(
  path: '/attendance/clock-in' | '/attendance/clock-out',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AttendanceActionInput) =>
      api.post<AttendanceRecord>(path, input).then((response) => response.data),
    onSuccess: (record) => {
      queryClient.setQueryData<StaffAttendance>(
        attendanceKeys.mine,
        (current) =>
          current
            ? {
                ...current,
                today: record,
                history:
                  record.state === 'complete'
                    ? [
                        record,
                        ...current.history.filter(
                          (item) => item.id !== record.id,
                        ),
                      ]
                    : current.history,
                clockInReasonRequired: false,
                clockOutReasonRequired: false,
              }
            : current,
      );
      void queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

export function useClockIn() {
  return useAttendanceAction('/attendance/clock-in');
}

export function useClockOut() {
  return useAttendanceAction('/attendance/clock-out');
}

export function useManagerAttendance(filters: AttendanceFilters = {}) {
  return useQuery({
    queryKey: attendanceKeys.manager(filters),
    queryFn: ({ signal }) =>
      api
        .get<AttendanceRecord[]>('/admin/attendance', {
          signal,
          query: {
            branchId: filters.branchId,
            from: filters.from,
            to: filters.to,
          },
        })
        .then((response) => response.data),
  });
}

/** RL-1803: POST /admin/attendance, a day the person did not clock. */
export interface AddAttendanceInput {
  staffUserId: string;
  branchId: string;
  clockedInAt: string;
  clockedOutAt: string;
  reason: string;
}

export function useAddAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddAttendanceInput) =>
      api
        .post<AttendanceRecord>('/admin/attendance', input)
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}

export function useCorrectAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: AttendanceCorrectionInput) =>
      api
        .patch<CorrectedAttendanceRecord>(`/admin/attendance/${id}`, input)
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: attendanceKeys.all }),
  });
}
